// SidebarMenu.tsx - Refactored with 3-level support + Tailwind CSS
import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { MenuItemType } from "@/constants/new-menu/types";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import "./SidebarMenu.css";
import clsx from "clsx";

interface SidebarMenuProps {
  menuItems: MenuItemType[];
  isCollapsed?: boolean;
  fallback?: () => void;
}

interface MenuItemProps {
  item: MenuItemType;
  level: number;
  isCollapsed: boolean;
  fallback?: () => void;
}

const SidebarMenu: React.FC<SidebarMenuProps> = ({
  menuItems = [],
  isCollapsed = false,
  fallback,
}) => {
  return (
    <nav className="sidebar-menu scrollbar-hide">
      {menuItems.map((item, index) => (
        <MenuItem
          key={`${item.key}-${index}`}
          item={item}
          level={1}
          isCollapsed={isCollapsed}
          fallback={fallback}
        />
      ))}
    </nav>
  );
};

const MenuItem: React.FC<MenuItemProps> = ({
  item,
  level,
  isCollapsed,
  fallback,
}) => {
  const { t } = useTranslation("navigation");
  const location = useLocation();
  const [isExpanded, setIsExpanded] = useState(false);

  // Check if this item or any descendant is active
  const isActive = checkIsActive(item, location.pathname);

  // Auto-expand if active
  useEffect(() => {
    if (isActive && item.children) {
      setIsExpanded(true);
    }
  }, [isActive, item.children]);

  const toggleExpand = () => {
    if (item.children) {
      setIsExpanded(!isExpanded);
      
      // If sidebar is collapsed and has children, expand sidebar
      if (isCollapsed && fallback) {
        fallback();
      }
    }
  };

  const handleClick = () => {
    if (item.children) {
      toggleExpand();
    }
  };

  // Different styling based on level
  const getLevelStyles = () => {
    switch (level) {
      case 1: // Parent
        return {
          wrapper: "",
          button: `
            menu-item
            flex items-center w-full px-3 py-3 rounded-l-full
            transition-all duration-200 group
            ${isActive 
              ? "active" 
              : "text-gray-800 font-medium hover:opacity-70"
            }
            ${item.disabled ? "opacity-30 pointer-events-none grayscale" : ""}
          `,
          icon: "w-5 h-5 flex-shrink-0",
          label: "text-base font-semibold truncate",
        };
      
      case 2: // Child
        return {
          wrapper: "ml-4",
          button: `
            flex items-center w-full px-3 py-2 rounded-md
            transition-all duration-200
            ${isActive 
              ? "text-primary font-bold" 
              : "!text-gray-800 font-medium hover:opacity-70 hover:bg-blue-100"
            }
            ${item.disabled ? "opacity-30 pointer-events-none grayscale" : ""}
          `,
          icon: "w-4 h-4 flex-shrink-0",
          label: "text-sm truncate",
        };
      
      case 3: // Grandchild
        return {
          wrapper: "ml-4",
          button: `
            flex items-center w-full px-3 py-2 rounded-md
            transition-all duration-200 text-sm
            ${isActive 
              ? "text-primary font-bold" 
              : "!text-gray-600 font-medium hover:opacity-70 hover:bg-blue-100"
            }
            ${item.disabled ? "opacity-30 pointer-events-none grayscale" : ""}
          `,
          icon: "w-4 h-4 flex-shrink-0",
          label: "text-sm truncate",
        };
      
      default:
        return {
          wrapper: "",
          button: "",
          icon: "",
          label: "",
        };
    }
  };

  const styles = getLevelStyles();

  // Render item with children (expandable)
  if (item.children && item.children.length > 0) {
    return (
      <div className={styles.wrapper}>
        <button
          onClick={handleClick}
          className={styles.button}
          type="button"
        >
          {/* Icon */}
          {item.icon && !isCollapsed && (
            <item.icon className={`${styles.icon} mr-3`} />
          )}
          {item.icon && isCollapsed && (
            <item.icon className={`${styles.icon}`} />
          )}

          {/* Label */}
          {!isCollapsed && (
            <span className={`${styles.label} flex-1 text-left pr-2`}>
              {t(item.label)}
            </span>
          )}

          {/* Expand/Collapse Icon */}
          {!isCollapsed && item.children && (
            <span className="ml-auto transition-transform duration-200">
              {isExpanded ? (
                <FaChevronDown className="w-3 h-3" />
              ) : (
                <FaChevronRight className="w-3 h-3" />
              )}
            </span>
          )}

          {/* Badge */}
          {!isCollapsed && item.badge && (
            <Badge badge={item.badge} />
          )}
        </button>

        {/* Children (recursive) */}
        {!isCollapsed && isExpanded && (
          <div className={clsx(
            level === 1 ? "my-1 space-y-0.5" : "mt-0.5 space-y-0.5"
          )}>
            {item.children.map((child, index) => (
              <MenuItem
                key={`${child.key}-${index}`}
                item={child}
                level={level + 1}
                isCollapsed={isCollapsed}
                fallback={fallback}
              />
            ))}
          </div>
        )}
      </div>
    );
  }

  // Render leaf item (no children)
  return (
    <div className={styles.wrapper}>
      <Link
        to={item.url || "#"}
        className={styles.button}
        onClick={() => {
          if (isCollapsed && fallback) {
            fallback();
          }
        }}
      >
        {/* Icon */}
        {item.icon && !isCollapsed && (
          <item.icon className={`${styles.icon} mr-3`} />
        )}
        {item.icon && isCollapsed && (
          <item.icon className={`${styles.icon}`} />
        )}

        {/* Label */}
        {!isCollapsed && (
          <span className={`${styles.label} flex-1 text-left`}>
            {t(item.label)}
          </span>
        )}

        {/* Badge */}
        {!isCollapsed && item.badge && (
          <Badge badge={item.badge} />
        )}
      </Link>
    </div>
  );
};

// Badge Component
const Badge: React.FC<{ badge: string | { text: string; variant: string } }> = ({
  badge,
}) => {
  if (typeof badge === "string") {
    return (
      <span
        className="
          inline-flex items-center justify-center
          min-w-[1.25rem] h-5 px-1.5
          text-xs font-medium rounded-full
          bg-gray-100 text-gray-800
        "
      >
        {badge}
      </span>
    );
  };
  
  const getVariantStyles = (variant: string) => {
    switch (variant) {
      case "primary":
        return "bg-blue-100 text-blue-800";
      case "secondary":
        return "bg-gray-100 text-gray-800";
      case "success":
        return "bg-green-100 text-green-800";
      case "danger":
        return "bg-red-100 text-red-800";
      case "warning":
        return "bg-yellow-100 text-yellow-800";
      case "info":
        return "bg-cyan-100 text-cyan-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span
      className={`
        inline-flex items-center justify-center
        min-w-[1.25rem] h-5 px-1.5
        text-xs font-medium rounded-full
        ${getVariantStyles(badge.variant)}
      `}
    >
      {badge.text}
    </span>
  );
};

// Helper function to check if item or any descendant is active
const checkIsActive = (item: MenuItemType, pathname: string): boolean => {
  // Check if current item is active
  if (item.url && pathname === item.url) {
    return true;
  }

  // Recursively check children
  if (item.children) {
    return item.children.some((child) => checkIsActive(child, pathname));
  }

  return false;
};

export default SidebarMenu;